import React, { useState, useRef, lazy } from 'react';
import { SearchIcon, ChevronLeftIcon, CheckCircle2Icon, CopyIcon, PlugIcon, FingerprintIcon, KeyIcon, KeyRoundIcon, DatabaseIcon, LockIcon, TerminalIcon } from 'lucide-react';
import { useMotionValueEvent, MotionValue, motion, useTransform } from 'framer-motion';
import { lerp } from '../scrollTimeline';
type Method = 'Sign in with' | 'API Token' | 'Personal Access Token' | 'Database Credentials' | 'Username & Password' | 'MCP' | 'Command Line Interface';
interface Integration {
  name: string;
  methods: Method[];
  logoDomain: string;
  icon?: React.ElementType;
}
const LOGO_KEY = 'pk_E8dUI9PzTHmmaUwDeUCyiQ';
function logoUrl(domain: string) {
  return `https://img.logo.dev/${domain}?token=${LOGO_KEY}&size=64`;
}
const INTEGRATIONS: Integration[] = [{
  name: 'Google Analytics',
  methods: ['Sign in with'],
  logoDomain: 'analytics.google.com'
}, {
  name: 'Google Drive',
  methods: ['Sign in with'],
  logoDomain: 'drive.google.com'
}, {
  name: 'Salesforce',
  methods: ['Sign in with'],
  logoDomain: 'salesforce.com'
}, {
  name: 'Slack',
  methods: ['Sign in with', 'MCP'],
  logoDomain: 'slack.com'
}, {
  name: 'GitHub',
  methods: ['MCP', 'Sign in with', 'Personal Access Token'],
  logoDomain: 'github.com'
}, {
  name: 'Notion',
  methods: ['API Token', 'MCP'],
  logoDomain: 'notion.so'
}, {
  name: 'Linear',
  methods: ['API Token'],
  logoDomain: 'linear.app'
}, {
  name: 'Figma',
  methods: ['MCP'],
  logoDomain: 'figma.com'
}, {
  name: 'Meta Ads',
  methods: ['MCP'],
  logoDomain: 'meta.com'
}, {
  name: 'JIRA',
  methods: ['API Token'],
  logoDomain: 'jira.com'
}, {
  name: 'Confluence',
  methods: ['API Token'],
  logoDomain: 'confluence.com'
}, {
  name: 'Stripe',
  methods: ['API Token'],
  logoDomain: 'stripe.com'
}, {
  name: 'Twilio',
  methods: ['API Token'],
  logoDomain: 'twilio.com'
}, {
  name: 'AWS CLI',
  methods: ['Command Line Interface'],
  logoDomain: 'aws.amazon.com'
}, {
  name: 'PostgreSQL',
  methods: ['Database Credentials'],
  logoDomain: 'postgresql.org'
}, {
  name: 'MySQL',
  methods: ['Database Credentials'],
  logoDomain: 'mysql.com'
}, {
  name: 'MongoDB',
  methods: ['Database Credentials'],
  logoDomain: 'mongodb.com'
}];
const METHOD_COLORS: Record<Method, string> = {
  'Sign in with': 'bg-green-50 text-green-700 border-green-200',
  'API Token': 'bg-blue-50 text-blue-700 border-blue-200',
  'Personal Access Token': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'Database Credentials': 'bg-orange-50 text-orange-700 border-orange-200',
  'Username & Password': 'bg-yellow-50 text-yellow-700 border-yellow-200',
  MCP: 'bg-purple-50 text-purple-700 border-purple-200',
  'Command Line Interface': 'bg-slate-50 text-slate-700 border-slate-200'
};
const METHOD_ICONS: Record<Method, React.ElementType> = {
  'Sign in with': LockIcon,
  'API Token': KeyIcon,
  'Personal Access Token': KeyRoundIcon,
  'Database Credentials': DatabaseIcon,
  'Username & Password': LockIcon,
  MCP: PlugIcon,
  'Command Line Interface': TerminalIcon
};
interface ConnectionSetupProps {
  scrollYProgress?: MotionValue<number>;
  contentStart: number;
  contentEnd: number;
}
export function ConnectionSetup({
  scrollYProgress,
  contentStart,
  contentEnd
}: ConnectionSetupProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState<Integration | null>(null);
  const [activeMethod, setActiveMethod] = useState<Method | null>(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectProgress, setConnectProgress] = useState(0);
  const [cliStep, setCliStep] = useState<'install' | 'installing' | 'authenticate'>('install');
  const [installProgress, setInstallProgress] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  // === SCROLL-DRIVEN DEMO ===
  // Adaptive demo: works with whatever state the user is in
  const [demoActive, setDemoActive] = useState(false);
  const [demoHighlight, setDemoHighlight] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fallbackMotionValue = useRef(new MotionValue<number>(0));
  const prevDemoPhase = useRef(-1);
  const demoDidSelect = useRef(false);
  const demoDidSetMethod = useRef(false);
  const demoDidInstall = useRef(false);
  // Phases — timing for adaptive state changes
  function getPhase(s: number): number {
    if (s < lerp(contentStart, contentEnd, 0.0)) return 0;
    if (s < lerp(contentStart, contentEnd, 0.28)) return 1;
    if (s < lerp(contentStart, contentEnd, 0.3)) return 2;
    if (s < lerp(contentStart, contentEnd, 0.32)) return 3;
    if (s < lerp(contentStart, contentEnd, 0.45)) return 4;
    if (s < lerp(contentStart, contentEnd, 0.47)) return 5;
    if (s < lerp(contentStart, contentEnd, 0.49)) return 6;
    if (s < lerp(contentStart, contentEnd, 0.6)) return 7;
    if (s < lerp(contentStart, contentEnd, 0.62)) return 8;
    if (s < lerp(contentStart, contentEnd, 0.8)) return 9;
    if (s < lerp(contentStart, contentEnd, 0.9)) return 10;
    return 11;
  }
  useMotionValueEvent(scrollYProgress ?? fallbackMotionValue.current, 'change', latest => {
    if (!scrollYProgress) return;
    // Outside demo range
    if (latest < contentStart - 0.005 || latest > contentEnd + 0.005) {
      if (prevDemoPhase.current !== -1) {
        setDemoActive(false);
        setDemoHighlight(null);
        prevDemoPhase.current = -1;
      }
      return;
    }
    const phase = getPhase(latest);
    const changed = phase !== prevDemoPhase.current;
    const forward = phase > prevDemoPhase.current;
    prevDemoPhase.current = phase;
    setDemoActive(phase >= 1);
    // --- Adaptive state logic with rewind support ---
    const isOnList = !selected;
    const hasMethodPicker = selected && selected.methods.length > 1 && !activeMethod;
    const hasActiveMethod = selected && activeMethod !== null;
    const firstMethod = selected?.methods[0] ?? null;
    const isMcpOrCli = activeMethod === 'MCP' || activeMethod === 'Command Line Interface';
    // Highlights
    if (phase === 1 && latest >= 0.195) {
      if (isOnList) {
        setDemoHighlight('GitHub');
      } else if (hasMethodPicker && firstMethod) {
        setDemoHighlight(firstMethod);
      } else {
        setDemoHighlight(null);
      }
    } else if (phase === 4 && latest >= 0.215) {
      if (hasMethodPicker && firstMethod) {
        setDemoHighlight(firstMethod);
      } else if (hasActiveMethod) {
        setDemoHighlight('install');
      } else {
        setDemoHighlight(null);
      }
    } else if (phase === 7 && latest >= 0.232) {
      setDemoHighlight('install');
    } else {
      setDemoHighlight(null);
    }
    // === REWIND ===
    if (!forward && changed) {
      if (phase <= 7 && demoDidInstall.current) {
        setCliStep('install');
        setInstallProgress(0);
        setConnected(false);
        demoDidInstall.current = false;
      }
      if (phase <= 4 && demoDidSetMethod.current) {
        setActiveMethod(null);
        setCliStep('install');
        setInstallProgress(0);
        setConnected(false);
        demoDidSetMethod.current = false;
        demoDidInstall.current = false;
      }
      if (phase <= 1 && demoDidSelect.current) {
        setSelected(null);
        setActiveMethod(null);
        setCliStep('install');
        setInstallProgress(0);
        setConnected(false);
        demoDidSelect.current = false;
        demoDidSetMethod.current = false;
        demoDidInstall.current = false;
      }
    }
    // === FORWARD ===
    if (phase === 3 || phase === 4) {
      if (isOnList) {
        const github = INTEGRATIONS.find(i => i.name === 'GitHub');
        if (github) {
          setSelected(github);
          demoDidSelect.current = true;
        }
      }
    } else if (phase === 6 || phase === 7) {
      if (hasMethodPicker && firstMethod) {
        setActiveMethod(firstMethod);
        demoDidSetMethod.current = true;
      }
    } else if (phase === 9) {
      if (hasActiveMethod && isMcpOrCli && cliStep !== 'authenticate') {
        const progress = Math.min(100, Math.max(0, (latest - 0.237) / 0.016 * 100));
        setInstallProgress(Math.round(progress));
        setCliStep(progress >= 100 ? 'authenticate' : 'installing');
        demoDidInstall.current = true;
      }
      if (hasActiveMethod && !isMcpOrCli && !connected && !connecting) {
        setConnected(true);
        demoDidInstall.current = true;
        setTimeout(() => setConnected(false), 3000);
      }
    } else if (phase === 11) {
      if (hasActiveMethod && isMcpOrCli && cliStep === 'authenticate' && !connected) {
        setConnected(true);
      }
    }
  });
  const filtered = INTEGRATIONS.filter(i => i.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleSelect = (integration: Integration) => {
    setSelected(integration);
    if (integration.methods.length === 1) {
      setActiveMethod(integration.methods[0]);
    } else {
      setActiveMethod(null);
    }
  };
  const handleConnect = (e: React.FormEvent) => {
    e.preventDefault();
    if (activeMethod === 'Database Credentials') {
      setConnecting(true);
      setConnectProgress(0);
      const interval = setInterval(() => {
        setConnectProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setConnecting(false);
            setConnected(true);
            setTimeout(() => {
              setConnected(false);
              setSelected(null);
              setActiveMethod(null);
              setConnectProgress(0);
            }, 2000);
            return 100;
          }
          return prev + 2;
        });
      }, 80);
    } else {
      setConnected(true);
      setTimeout(() => {
        setConnected(false);
        setSelected(null);
        setActiveMethod(null);
      }, 2000);
    }
  };
  const handleInstall = () => {
    setCliStep('installing');
    setInstallProgress(0);
    const interval = setInterval(() => {
      setInstallProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setCliStep('authenticate');
          return 100;
        }
        return prev + 1;
      });
    }, 100);
  };
  const handleBack = () => {
    if (activeMethod && selected && selected.methods.length > 1) {
      setActiveMethod(null);
    } else {
      setSelected(null);
      setActiveMethod(null);
      setConnected(false);
      setCliStep('install');
      setInstallProgress(0);
    }
  };
  return <div ref={containerRef} className="w-full max-w-[800px] mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden border border-neutral-200 flex flex-col h-[65vh] max-h-[600px] relative" data-id="element-75">
      {!selected ? <div className="flex flex-col h-full" data-id="element-76">
          <div className="px-5 pt-4 pb-3 border-b border-neutral-100 shrink-0" data-id="element-77">
            <p className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold mb-2" data-id="element-78">
              Add Connection
            </p>
            <div className="relative" data-id="element-79">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" data-id="element-80" />
              <input ref={searchRef} type="text" placeholder="Search integrations..." value={searchQuery} onChange={e => {
            setSearchQuery(e.target.value);
          }} className="w-full pl-9 pr-4 py-2 bg-neutral-50 border border-neutral-200 rounded-lg text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all" data-id="element-81" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 bg-neutral-50/50" data-id="element-82">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-id="element-83">
              {filtered.map(integration => <button key={integration.name} onClick={() => handleSelect(integration)} className={`flex items-center gap-3 p-3 bg-white border rounded-xl transition-all text-left group ${demoHighlight === integration.name ? 'border-blue-400 shadow-md shadow-blue-100 ring-2 ring-blue-200' : 'border-neutral-200 hover:border-blue-300 hover:shadow-sm'}`} data-id="element-84">
                  <div className="w-9 h-9 rounded-lg bg-neutral-50 border border-neutral-100 flex items-center justify-center shrink-0 overflow-hidden group-hover:border-blue-100 transition-colors" data-id="element-85">
                    <img src={logoUrl(integration.logoDomain)} alt={integration.name} className="w-5 h-5 object-contain" loading="lazy" data-id="element-86" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-87">
                    <div className="flex items-center gap-1.5" data-id="element-88">
                      <h3 className="text-base font-semibold text-neutral-900 truncate" data-id="element-89">
                        {integration.name}
                      </h3>
                      {integration.methods.map(method => {
                  const MethodIcon = METHOD_ICONS[method];
                  return <span key={method} className="relative w-5 h-5 rounded-full border border-neutral-200 bg-neutral-50 inline-flex items-center justify-center shrink-0 group/tip" data-id="element-90">
                            <MethodIcon className="w-3 h-3 text-neutral-500" data-id="element-91" />
                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-neutral-900 text-white text-[10px] font-medium rounded-md whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity pointer-events-none shadow-lg z-10" data-id="element-92">
                              {method}
                            </span>
                          </span>;
                })}
                    </div>
                  </div>
                </button>)}
              {filtered.length === 0 && <div className="col-span-full text-center py-8 text-neutral-400 text-sm" data-id="element-93">
                  No integrations found matching "{searchQuery}"
                </div>}
            </div>
          </div>
        </div> : <div className="flex flex-col h-full" data-id="element-94">
          <div className="px-5 py-3 border-b border-neutral-100 shrink-0 flex items-center gap-3" data-id="element-95">
            <button onClick={handleBack} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-neutral-100 text-neutral-500 transition-colors" data-id="element-96">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-97" />
            </button>
            <div className="flex items-center gap-2.5" data-id="element-98">
              <div className="w-7 h-7 rounded-md bg-neutral-50 border border-neutral-100 flex items-center justify-center overflow-hidden" data-id="element-99">
                <img src={logoUrl(selected.logoDomain)} alt={selected.name} className="w-4 h-4 object-contain" data-id="element-100" />
              </div>
              <h2 className="text-base font-bold text-neutral-900" data-id="element-101">
                Connect {selected.name}
              </h2>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-5 bg-neutral-50/50 flex items-center justify-center" data-id="element-102">
            <div className="w-full max-w-md bg-white border border-neutral-200 rounded-2xl p-5 shadow-sm" data-id="element-103">
              {connecting ? <div className="flex flex-col items-center justify-center py-6 text-center" data-id="element-104">
                  <p className="text-sm font-medium text-neutral-700 mb-4" data-id="element-105">
                    Connecting to {selected.name}...
                  </p>
                  <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden mb-2" data-id="element-106">
                    <div className="h-full bg-[#0f172a] rounded-full transition-all duration-75 ease-linear" style={{
                width: `${connectProgress}%`
              }} data-id="element-107" />
                  </div>
                  <p className="text-xs text-neutral-400" data-id="element-108">{connectProgress}%</p>
                </div> : connected ? <div className="flex flex-col items-center justify-center py-6 text-center" data-id="element-109">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mb-3" data-id="element-110">
                    <CheckCircle2Icon className="w-5 h-5 text-green-600" data-id="element-111" />
                  </div>
                  <h3 className="text-base font-semibold text-neutral-900 mb-1" data-id="element-112">
                    Connected Successfully
                  </h3>
                  <p className="text-xs text-neutral-500" data-id="element-113">
                    {selected.name} is now synced with GigaBrain.
                  </p>
                </div> : !activeMethod ? <div className="flex flex-col gap-3" data-id="element-114">
                  <h3 className="text-sm font-semibold text-neutral-900 mb-1" data-id="element-115">
                    Choose connection method
                  </h3>
                  {selected.methods.map(method => <button key={method} onClick={() => {
              setActiveMethod(method);
            }} className={`w-full p-3 rounded-xl border text-left flex items-center gap-3 transition-all ${METHOD_COLORS[method]} ${demoHighlight === method ? 'ring-2 ring-blue-300 shadow-md shadow-blue-100 scale-[1.02]' : 'hover:shadow-sm'}`} data-id="element-116">
                      <PlugIcon className="w-4 h-4 shrink-0" data-id="element-117" />
                      <span className="font-medium text-sm" data-id="element-118">{method}</span>
                    </button>)}
                </div> : <form onSubmit={handleConnect} className="flex flex-col gap-3" data-id="element-119">
                  <div className="mb-1" data-id="element-120">
                    <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium border ${METHOD_COLORS[activeMethod]}`} data-id="element-121">
                      {activeMethod}
                    </span>
                  </div>

                  {activeMethod === 'Sign in with' && <div className="py-3" data-id="element-122">
                      <p className="text-sm text-neutral-600 mb-4" data-id="element-123">
                        You'll be redirected to {selected.name} to authorize
                        access.
                      </p>
                      <button type="submit" className="w-full py-2.5 px-4 bg-[#0f172a] hover:bg-[#1e293b] text-white rounded-xl font-medium transition-colors text-sm" data-id="element-124">
                        Sign in with {selected.name}
                      </button>
                    </div>}

                  {(activeMethod === 'API Token' || activeMethod === 'Personal Access Token') && <>
                      <div data-id="element-125">
                        <label className="block text-xs font-medium text-neutral-700 mb-1" data-id="element-126">
                          {activeMethod === 'Personal Access Token' ? 'Personal Access Token' : 'Access Token'}
                        </label>
                        <input type="text" required placeholder={activeMethod === 'Personal Access Token' ? 'ghp_...' : 'ntn_...'} className="w-full px-3 py-2 bg-neutral-50 border border-neutral-200 rounded-lg text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" onFocus={e => {
                  if (!e.target.value) e.target.value = activeMethod === 'Personal Access Token' ? 'ghp_a1b2c3d4e5f6g7h8i9j0' : 'ntn_v2_abc123def456ghi789jkl012';
                }} data-id="element-127" />
                      </div>
                      <button type="submit" className="w-full py-2 px-4 bg-[#0f172a] hover:bg-[#1e293b] text-white rounded-xl font-medium transition-colors text-sm mt-1" data-id="element-128">
                        Connect
                      </button>
                    </>}

                  {activeMethod === 'Database Credentials' && <>
                      <div className="grid grid-cols-2 gap-3" data-id="element-129">
                        <div data-id="element-130">
                          <label className="block text-xs font-medium text-neutral-700 mb-1" data-id="element-131">
                            Host
                          </label>
                          <input type="text" required placeholder="db.example.com" className="w-full px-3 py-2 bg-neutral-50 border border-neutral-200 rounded-lg text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" onFocus={e => {
                    if (!e.target.value) e.target.value = 'prod-db-01.us-east-1.rds.amazonaws.com';
                  }} data-id="element-132" />
                        </div>
                        <div data-id="element-133">
                          <label className="block text-xs font-medium text-neutral-700 mb-1" data-id="element-134">
                            Port
                          </label>
                          <input type="text" required placeholder="5432" className="w-full px-3 py-2 bg-neutral-50 border border-neutral-200 rounded-lg text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" onFocus={e => {
                    if (!e.target.value) e.target.value = '5432';
                  }} data-id="element-135" />
                        </div>
                      </div>
                      <div data-id="element-136">
                        <label className="block text-xs font-medium text-neutral-700 mb-1" data-id="element-137">
                          Database Name
                        </label>
                        <input type="text" required placeholder="production_db" className="w-full px-3 py-2 bg-neutral-50 border border-neutral-200 rounded-lg text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" onFocus={e => {
                  if (!e.target.value) e.target.value = 'gigabrain_prod';
                }} data-id="element-138" />
                      </div>
                      <div className="grid grid-cols-2 gap-3" data-id="element-139">
                        <div data-id="element-140">
                          <label className="block text-xs font-medium text-neutral-700 mb-1" data-id="element-141">
                            Username
                          </label>
                          <input type="text" required placeholder="readonly_user" className="w-full px-3 py-2 bg-neutral-50 border border-neutral-200 rounded-lg text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" onFocus={e => {
                    if (!e.target.value) e.target.value = 'giga_readonly';
                  }} data-id="element-142" />
                        </div>
                        <div data-id="element-143">
                          <label className="block text-xs font-medium text-neutral-700 mb-1" data-id="element-144">
                            Password
                          </label>
                          <input type="password" required placeholder="••••••••" className="w-full px-3 py-2 bg-neutral-50 border border-neutral-200 rounded-lg text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" onFocus={e => {
                    if (!e.target.value) e.target.value = 'sup3r_s3cur3_pw!';
                  }} data-id="element-145" />
                        </div>
                      </div>
                      <label className="flex items-center gap-2 mt-2 cursor-pointer" data-id="element-146">
                        <input type="checkbox" className="w-4 h-4 rounded border-neutral-300 text-[#0f172a] focus:ring-blue-500/20" data-id="element-147" />
                        <span className="text-xs text-neutral-600" data-id="element-148">
                          Allow writes
                        </span>
                      </label>
                      <button type="submit" className="w-full py-2 px-4 bg-[#0f172a] hover:bg-[#1e293b] text-white rounded-xl font-medium transition-colors text-sm mt-1" data-id="element-149">
                        Connect Database
                      </button>
                    </>}

                  {activeMethod === 'Username & Password' && <>
                      <div data-id="element-150">
                        <label className="block text-xs font-medium text-neutral-700 mb-1" data-id="element-151">
                          Username or Email
                        </label>
                        <input type="text" required placeholder="admin@company.com" className="w-full px-3 py-2 bg-neutral-50 border border-neutral-200 rounded-lg text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" onFocus={e => {
                  if (!e.target.value) e.target.value = 'sarah@acme.co';
                }} data-id="element-152" />
                      </div>
                      <div data-id="element-153">
                        <label className="block text-xs font-medium text-neutral-700 mb-1" data-id="element-154">
                          Password
                        </label>
                        <input type="password" required placeholder="••••••••" className="w-full px-3 py-2 bg-neutral-50 border border-neutral-200 rounded-lg text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" onFocus={e => {
                  if (!e.target.value) e.target.value = 'j1r4_p4ssw0rd!';
                }} data-id="element-155" />
                      </div>
                      <button type="submit" className="w-full py-2 px-4 bg-[#0f172a] hover:bg-[#1e293b] text-white rounded-xl font-medium transition-colors text-sm mt-1" data-id="element-156">
                        Connect
                      </button>
                    </>}

                  {activeMethod === 'MCP' && <div className="py-2" data-id="element-157">
                      {cliStep === 'install' && <>
                          <p className="text-sm text-neutral-600 mb-4" data-id="element-158">
                            Install the {selected.name} MCP connector to enable
                            two-way sync.
                          </p>
                          <button type="button" onClick={handleInstall} className={`w-full py-2.5 px-4 bg-[#0f172a] hover:bg-[#1e293b] text-white rounded-xl font-medium transition-all text-sm ${demoHighlight === 'install' ? 'ring-2 ring-blue-400 ring-offset-2 scale-[1.02]' : ''}`} data-id="element-159">
                            Install {selected.name} MCP
                          </button>
                        </>}
                      {cliStep === 'installing' && <>
                          <p className="text-sm text-neutral-600 mb-4" data-id="element-160">
                            Installing {selected.name} MCP connector...
                          </p>
                          <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden mb-2" data-id="element-161">
                            <div className="h-full bg-[#0f172a] rounded-full transition-all duration-100 ease-linear" style={{
                    width: `${installProgress}%`
                  }} data-id="element-162" />
                          </div>
                          <p className="text-xs text-neutral-400 text-center" data-id="element-163">
                            {installProgress}%
                          </p>
                        </>}
                      {cliStep === 'authenticate' && <>
                          <div className="flex items-center gap-2 mb-4" data-id="element-164">
                            <CheckCircle2Icon className="w-4 h-4 text-green-600 shrink-0" data-id="element-165" />
                            <p className="text-sm text-green-700 font-medium" data-id="element-166">
                              {selected.name} MCP connector installed
                            </p>
                          </div>
                          <button type="submit" className="w-full py-2.5 px-4 bg-[#0f172a] hover:bg-[#1e293b] text-white rounded-xl font-medium transition-colors text-sm" data-id="element-167">
                            Authenticate
                          </button>
                        </>}
                    </div>}

                  {activeMethod === 'Command Line Interface' && <div className="py-2" data-id="element-168">
                      {cliStep === 'install' && <>
                          <p className="text-sm text-neutral-600 mb-4" data-id="element-169">
                            Install the {selected.name} CLI tool to connect with
                            GigaBrain.
                          </p>
                          <button type="button" onClick={handleInstall} className="w-full py-2.5 px-4 bg-[#0f172a] hover:bg-[#1e293b] text-white rounded-xl font-medium transition-colors text-sm" data-id="element-170">
                            Install {selected.name}
                          </button>
                        </>}
                      {cliStep === 'installing' && <>
                          <p className="text-sm text-neutral-600 mb-4" data-id="element-171">
                            Installing {selected.name}...
                          </p>
                          <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden mb-2" data-id="element-172">
                            <div className="h-full bg-[#0f172a] rounded-full transition-all duration-100 ease-linear" style={{
                    width: `${installProgress}%`
                  }} data-id="element-173" />
                          </div>
                          <p className="text-xs text-neutral-400 text-center" data-id="element-174">
                            {installProgress}%
                          </p>
                        </>}
                      {cliStep === 'authenticate' && <>
                          <div className="flex items-center gap-2 mb-4" data-id="element-175">
                            <CheckCircle2Icon className="w-4 h-4 text-green-600 shrink-0" data-id="element-176" />
                            <p className="text-sm text-green-700 font-medium" data-id="element-177">
                              {selected.name} installed successfully
                            </p>
                          </div>
                          <button type="submit" className="w-full py-2.5 px-4 bg-[#0f172a] hover:bg-[#1e293b] text-white rounded-xl font-medium transition-colors text-sm" data-id="element-178">
                            Authenticate
                          </button>
                        </>}
                    </div>}
                </form>}
            </div>
          </div>
        </div>}
    </div>;
}